import React, { useState, useEffect, useRef } from "react";
import { ReligionTab } from "../types";
import { createAudioContext } from "../lib/audio-utils";
import { Wind, Play, Pause, RotateCcw, Volume2, VolumeX, Sparkles, Heart, Info, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

interface PranayamaBreathingProps {
  activeReligion?: ReligionTab;
}

export type BreathPhase = "inhale" | "holdIn" | "exhale" | "holdOut";

export interface BreathTechnique {
  id: string;
  name: string;
  sanskritName: string;
  description: string;
  benefits: string;
  phases: {
    inhale: number; // seconds
    holdIn: number;
    exhale: number;
    holdOut: number;
  };
}

const BREATH_TECHNIQUES: BreathTechnique[] = [
  {
    id: "nadi_shodhana",
    name: "Nadi Shodhana (Alternate Nostril)",
    sanskritName: "नाडीशोधन प्राणायाम",
    description: "Balances Ida & Pingala energy channels (Nadis) before sacred mantra chanting.",
    benefits: "Calms nervous system, balances hemisphere focus, reduces heart rate variability.",
    phases: { inhale: 4, holdIn: 4, exhale: 4, holdOut: 4 }
  },
  {
    id: "box_breathing",
    name: "Sama Vritti (Equal / Box Breathing)",
    sanskritName: "समवृत्ति प्राणायाम",
    description: "Four equal phase cadence for profound physical stillness and mental focus.",
    benefits: "Eliminates anxiety, stills wandering thoughts, prepares the mind for deep Japa.",
    phases: { inhale: 4, holdIn: 4, exhale: 4, holdOut: 4 }
  },
  {
    id: "478_relax",
    name: "4-7-8 Tranquility Breath",
    sanskritName: "शान्ति प्राणायाम",
    description: "Deep relaxation sequence with extended exhale for parasympathetic activation.",
    benefits: "Induces deep state of spiritual surrender, eases stress, prepares heart for prayer.",
    phases: { inhale: 4, holdIn: 7, exhale: 8, holdOut: 0 }
  },
  {
    id: "kapalabhati",
    name: "Kapalabhati (Skull Shining Breath)",
    sanskritName: "कपालभाति प्राणायाम",
    description: "Vigorous cleansing breath generating internal Agni (vital warmth and clarity).",
    benefits: "Clears respiratory passages, heightens alertness, ignites spiritual fire.",
    phases: { inhale: 2, holdIn: 1, exhale: 2, holdOut: 1 }
  }
];

export default function PranayamaBreathing({ activeReligion }: PranayamaBreathingProps) {
  const [selectedTechniqueId, setSelectedTechniqueId] = useState<string>("nadi_shodhana");
  const [isActive, setIsActive] = useState<boolean>(false);
  const [currentPhase, setCurrentPhase] = useState<BreathPhase>("inhale");
  const [secondsRemaining, setSecondsRemaining] = useState<number>(4);
  const [completedCycles, setCompletedCycles] = useState<number>(0);
  const [soundEnabled, setSoundEnabled] = useState<boolean>(true);

  const technique = BREATH_TECHNIQUES.find((t) => t.id === selectedTechniqueId) || BREATH_TECHNIQUES[0];

  // Soft soft chime for breath phase transitions
  const playPhaseChime = (phase: BreathPhase) => {
    if (!soundEnabled) return;
    try {
      const ctx = createAudioContext();
      if (!ctx) return;

      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      let freq = 440; // Inhale pitch
      if (phase === "holdIn") freq = 554.37; // C#5
      if (phase === "exhale") freq = 330; // E4
      if (phase === "holdOut") freq = 277.18; // C#4

      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now);

      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.15, now + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.8);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.8);
    } catch (e) {}
  };

  // State machine timer logic
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isActive) {
      interval = setInterval(() => {
        setSecondsRemaining((prev) => {
          if (prev > 1) {
            return prev - 1;
          }

          // Advance phase
          let nextPhase: BreathPhase = "inhale";
          let nextSecs = 4;

          if (currentPhase === "inhale") {
            if (technique.phases.holdIn > 0) {
              nextPhase = "holdIn";
              nextSecs = technique.phases.holdIn;
            } else {
              nextPhase = "exhale";
              nextSecs = technique.phases.exhale;
            }
          } else if (currentPhase === "holdIn") {
            nextPhase = "exhale";
            nextSecs = technique.phases.exhale;
          } else if (currentPhase === "exhale") {
            if (technique.phases.holdOut > 0) {
              nextPhase = "holdOut";
              nextSecs = technique.phases.holdOut;
            } else {
              nextPhase = "inhale";
              nextSecs = technique.phases.inhale;
              setCompletedCycles((c) => c + 1);
            }
          } else if (currentPhase === "holdOut") {
            nextPhase = "inhale";
            nextSecs = technique.phases.inhale;
            setCompletedCycles((c) => c + 1);
          }

          setCurrentPhase(nextPhase);
          playPhaseChime(nextPhase);
          return nextSecs;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, currentPhase, technique, soundEnabled]);

  const handleStart = () => {
    setIsActive(true);
    setCurrentPhase("inhale");
    setSecondsRemaining(technique.phases.inhale);
    playPhaseChime("inhale");
  };

  const handlePause = () => {
    setIsActive(false);
  };

  const handleReset = () => {
    setIsActive(false);
    setCurrentPhase("inhale");
    setSecondsRemaining(technique.phases.inhale);
    setCompletedCycles(0);
  };

  const handleSelectTechnique = (id: string) => {
    setSelectedTechniqueId(id);
    setIsActive(false);
    const newTech = BREATH_TECHNIQUES.find((t) => t.id === id) || BREATH_TECHNIQUES[0];
    setCurrentPhase("inhale");
    setSecondsRemaining(newTech.phases.inhale);
    setCompletedCycles(0);
  };

  // Phase Display Text & Color
  const getPhaseInfo = () => {
    switch (currentPhase) {
      case "inhale":
        return { label: "Inhale Slowly (Puraka)", color: "from-amber-400 to-orange-500", scale: 1.25 };
      case "holdIn":
        return { label: "Hold Breath (Antar Kumbhaka)", color: "from-amber-300 to-yellow-400", scale: 1.25 };
      case "exhale":
        return { label: "Exhale Completely (Rechaka)", color: "from-blue-400 to-indigo-500", scale: 0.8 };
      case "holdOut":
        return { label: "Rest in Stillness (Bahya Kumbhaka)", color: "from-purple-400 to-indigo-400", scale: 0.8 };
    }
  };

  const phaseInfo = getPhaseInfo();

  return (
    <div className="rounded-2xl bg-gradient-to-br from-stone-900 via-stone-900/90 to-amber-950/30 border border-amber-500/20 p-5 shadow-lg backdrop-blur-md space-y-5">
      {/* Module Title */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-stone-800 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400">
            <Wind className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-stone-100 tracking-wide">Pranayama Sacred Breathwork</h3>
            <p className="text-xs text-stone-400">
              Prepare your respiratory rhythm & bio-energy before mantra chanting or meditation.
            </p>
          </div>
        </div>

        <button
          onClick={() => setSoundEnabled(!soundEnabled)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-colors ${
            soundEnabled
              ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
              : "bg-stone-800 border-stone-700 text-stone-500"
          }`}
        >
          {soundEnabled ? <Volume2 className="w-3.5 h-3.5 text-amber-400" /> : <VolumeX className="w-3.5 h-3.5" />}
          <span>{soundEnabled ? "Chime On" : "Chime Muted"}</span>
        </button>
      </div>

      {/* Technique Selector Tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {BREATH_TECHNIQUES.map((tech) => {
          const isSelected = tech.id === selectedTechniqueId;
          return (
            <button
              key={tech.id}
              onClick={() => handleSelectTechnique(tech.id)}
              className={`p-3 rounded-xl border text-left transition-all ${
                isSelected
                  ? "bg-gradient-to-r from-amber-950/80 to-orange-950/60 border-amber-500/50 text-amber-200 shadow-md shadow-amber-950/20"
                  : "bg-stone-900/60 border-stone-800 text-stone-400 hover:text-stone-200 hover:bg-stone-800/50"
              }`}
            >
              <p className="text-xs font-bold truncate">{tech.name}</p>
              <p className="text-[10px] text-amber-400/80 font-serif mt-0.5 truncate">{tech.sanskritName}</p>
            </button>
          );
        })}
      </div>

      {/* Interactive Visualizer Stage */}
      <div className="flex flex-col md:flex-row items-center justify-around gap-6 bg-stone-950/80 border border-stone-800/80 rounded-2xl p-6 relative overflow-hidden">
        {/* Ambient background aura */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />

        {/* Breathing Animation Circle */}
        <div className="relative flex items-center justify-center w-52 h-52 shrink-0">
          {/* Pulsing Outer Rings */}
          <motion.div
            animate={{
              scale: isActive ? phaseInfo.scale : 1,
              opacity: isActive ? 0.8 : 0.3
            }}
            transition={{
              duration: secondsRemaining,
              ease: "easeInOut"
            }}
            className={`absolute inset-0 rounded-full bg-gradient-to-tr ${phaseInfo.color} opacity-20 blur-xl`}
          />

          <motion.div
            animate={{
              scale: isActive ? phaseInfo.scale : 1
            }}
            transition={{
              duration: secondsRemaining,
              ease: "easeInOut"
            }}
            className={`w-36 h-36 rounded-full bg-gradient-to-tr ${phaseInfo.color} p-1 shadow-2xl flex items-center justify-center transition-shadow`}
          >
            <div className="w-full h-full bg-stone-950 rounded-full flex flex-col items-center justify-center p-2 text-center shadow-inner">
              <span className="text-3xl font-black font-mono text-amber-100">{secondsRemaining}s</span>
              <span className="text-[10px] font-bold text-amber-400/90 uppercase tracking-widest mt-0.5">
                {currentPhase}
              </span>
            </div>
          </motion.div>
        </div>

        {/* Status, Phase Description & Controls */}
        <div className="flex-1 space-y-4 text-center md:text-left z-10">
          <div>
            <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <h4 className="text-base font-bold text-amber-200">{technique.name}</h4>
            </div>
            <p className="text-xs text-stone-300 leading-relaxed max-w-md">{technique.description}</p>
          </div>

          <div className="p-3 rounded-xl bg-stone-900/90 border border-amber-500/20 text-xs text-amber-300/90 flex items-center gap-2">
            <Info className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="font-medium">{phaseInfo.label}</span>
          </div>

          <div className="flex items-center justify-center md:justify-start gap-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-stone-400 font-mono">
                Cycles Completed: <strong className="text-amber-200 font-bold">{completedCycles}</strong>
              </span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center md:justify-start gap-3 pt-2">
            {!isActive ? (
              <button
                onClick={handleStart}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 border border-amber-400/50 text-white font-bold text-xs shadow-lg shadow-amber-900/30 hover:scale-105 active:scale-95 transition-all"
              >
                <Play className="w-4 h-4 fill-white" />
                <span>Start Breathwork</span>
              </button>
            ) : (
              <button
                onClick={handlePause}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-stone-800 border border-stone-700 text-stone-200 font-bold text-xs shadow-md hover:bg-stone-700 transition-all"
              >
                <Pause className="w-4 h-4 fill-stone-200" />
                <span>Pause</span>
              </button>
            )}

            <button
              onClick={handleReset}
              className="p-2.5 rounded-xl bg-stone-900 border border-stone-800 text-stone-400 hover:text-amber-300 hover:border-amber-500/30 transition-colors"
              title="Reset Cycles"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
